import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Chrome } from "lucide-react";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginEmail, loginPassword);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(regEmail, regPassword, regName);
      toast.success("Account created");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen w-full grid md:grid-cols-2 bg-background">
      {/* Left visual pane */}
      <div className="hidden md:flex relative overflow-hidden bg-primary text-primary-foreground p-12 flex-col justify-between grain-bg" data-testid="login-visual-pane">
        <div className="relative z-10">
          <div className="flex items-center gap-2 text-primary-foreground/90">
            <span className="font-display text-lg tracking-tight">PurseImburse</span>
          </div>
        </div>
        <div className="relative z-10 space-y-6 max-w-md">
          <h1 className="font-display text-5xl lg:text-6xl font-light tracking-tighter leading-[1.05]">
            Snap it.<br/>
            <span className="font-semibold">Track it.</span><br/>
            Get reimbursed.
          </h1>
          <p className="text-primary-foreground/80 leading-relaxed">
            Photograph a receipt. AI reads the total. Export a print-ready PDF at the end of every quarter. No tedious spreadsheets. No missed reimbursements.
          </p>
        </div>
        <div className="relative z-10 text-xs uppercase tracking-[0.25em] text-primary-foreground/60">
          Personal expense tracker · Quarterly reports
        </div>
      </div>

      {/* Right form pane */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md space-y-6">
          <div className="md:hidden flex items-center gap-2 text-primary">
            <span className="font-display text-lg tracking-tight">PurseImburse</span>
          </div>

          <div className="space-y-1">
            <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground">Welcome</h2>
            <p className="text-sm text-muted-foreground">Sign in to track your quarterly expenses.</p>
          </div>

          <Button
            variant="outline"
            className="w-full rounded-full h-11"
            onClick={handleGoogle}
            data-testid="google-login-btn"
          >
            <Chrome className="w-4 h-4 mr-2" /> Continue with Google
          </Button>

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <Card className="p-6 border-border/60 shadow-sm">
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="login" data-testid="tab-login">Sign in</TabsTrigger>
                <TabsTrigger value="register" data-testid="tab-register">Create account</TabsTrigger>
              </TabsList>
              <TabsContent value="login" className="mt-4">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input id="login-email" type="email" required value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} data-testid="login-email-input" placeholder="you@company.com" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input id="login-password" type="password" required value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} data-testid="login-password-input" />
                  </div>
                  <Button type="submit" className="w-full rounded-full h-11" disabled={loading} data-testid="login-submit-btn">
                    {loading ? "Signing in…" : "Sign in"}
                  </Button>
                </form>
              </TabsContent>
              <TabsContent value="register" className="mt-4">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reg-name">Name</Label>
                    <Input id="reg-name" required value={regName} onChange={(e) => setRegName(e.target.value)} data-testid="register-name-input" placeholder="Sarah Miller" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-email">Email</Label>
                    <Input id="reg-email" type="email" required value={regEmail} onChange={(e) => setRegEmail(e.target.value)} data-testid="register-email-input" placeholder="you@company.com" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-password">Password</Label>
                    <Input id="reg-password" type="password" required minLength={6} value={regPassword} onChange={(e) => setRegPassword(e.target.value)} data-testid="register-password-input" />
                  </div>
                  <Button type="submit" className="w-full rounded-full h-11" disabled={loading} data-testid="register-submit-btn">
                    {loading ? "Creating…" : "Create account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </Card>
          <p className="text-xs text-muted-foreground text-center">
            <Link to="/" className="hover:text-foreground">← Back home</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
